import './modal-task-view.scss';
import React from 'react';
import Modal from '@mui/material/Modal';
import {Alert, Box, Snackbar} from '@mui/material';
import {useTranslation} from 'react-i18next';
import apiClient from '../../../../utils/apiClient';
import {jwtDecode} from 'jwt-decode';
import dayjs from 'dayjs';
import CalendarMemberContext from './CalendarMemberContext';
import { ReminderDetailContent } from './ReminderDetailContent';
import { getCreatorId, getOwnerId, isSystemCreator, resolvePersonName } from './reminderDetails.utils';
import { saveCalendarReturnContext } from '../new-calendar/calendarSessionState';

function ModalTaskView({
  open,
  onClose,
  task,
  onEdit,
  onRemove,
  onCompletionError,
  onCompletionStart,
  onCompletionSuccess,
  initialMember,
}) {
  const {t} = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const decodedToken = React.useMemo(() => {
    if (!token) return null;
    try {
      return jwtDecode(token);
    } catch (_error) {
      return null;
    }
  }, [token]);
  const loggedUser = JSON.parse(sessionStorage.getItem('loggedUser') || 'null');
  const userArea = loggedUser?.area_code;
  const pmaId = decodedToken?.id;

  const [taskFull, setTaskFull] = React.useState(task || null);
  const [owners, setOwners] = React.useState([]);
  const [operationError, setOperationError] = React.useState('');
  const [isCompleting, setIsCompleting] = React.useState(false);
  const [selectedMember, setSelectedMember] = React.useState(null);

  React.useEffect(() => {
    setTaskFull(task || null);
    setSelectedMember(initialMember || null);
  }, [initialMember, task]);

  React.useEffect(() => {
    if (!open || !token || !pmaId) return;

    let alive = true;

    (async () => {
      try {
        const config = {headers: {Authorization: `Bearer ${token}`}};
        const ownersRes = await apiClient.get(`${process.env.REACT_APP_API_URL}/pma/all`, config);

        if (!alive) return;
        setOwners(Array.isArray(ownersRes?.data) ? ownersRes.data : []);
      } catch (e) {
        if (!alive) return;
        setOwners([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, token, pmaId]);

  React.useEffect(() => {
    if (!open || !task?.id || !token) return;

    let alive = true;

    (async () => {
      try {
        const res = await apiClient.get(`${process.env.REACT_APP_API_URL}/calendar/event/${task.id}`, {
          headers: {Authorization: `Bearer ${token}`},
        });

        if (!alive) return;

        const details = res?.data ?? {};
        setTaskFull((prev) => ({
          ...(prev || {}),
          ...(details || {}),
          id: task.id,
        }));
      } catch (e) {
        if (!alive) return;
        setOperationError(t('calendar-detail-load-error'));
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, task?.id, token, t]);

  const isCompleted = React.useMemo(() => {
    const v = taskFull?.completed;
    return v === true || v === 1 || v === '1';
  }, [taskFull?.completed]);

  const dateLine = taskFull?.starting_date
    ? dayjs(taskFull.starting_date).locale(userArea?.toLowerCase() || 'en').format('dddd, D MMMM, YYYY')
    : '';

  const timeLine = taskFull?.starting_time ? dayjs(taskFull.starting_time, ['HH:mm:ss', 'HH:mm']).format('HH:mm') : '';
  const dateTimeLabel = taskFull?.all_day ? t('calendar-reminder-all-day') : timeLine;
  const categoryLabel = String(taskFull?.category_name ?? '').trim();
  const completedAtLabel = taskFull?.completed_at
    ? dayjs(taskFull.completed_at).locale(userArea?.toLowerCase() || 'en').format('D MMMM YYYY, HH:mm')
    : '';

  const creatorLabel = React.useMemo(() => {
    return resolvePersonName({
      explicitName: taskFull?.created_by_name,
      people: owners,
      reference: taskFull?.created_by_id ?? taskFull?.created_by ?? taskFull?.createdBy,
    });
  }, [taskFull?.created_by_name, taskFull?.created_by_id, taskFull?.created_by, taskFull?.createdBy, owners]);

  const ownerLabel = React.useMemo(() => {
    return resolvePersonName({
      explicitName: taskFull?.owner_name,
      people: owners,
      reference: taskFull?.owner_id ?? taskFull?.owner,
    });
  }, [taskFull?.owner_name, taskFull?.owner_id, taskFull?.owner, owners]);

  const hacLabels = React.useMemo(() => {
    const assigned = Array.isArray(taskFull?.assigned_hacs) ? taskFull.assigned_hacs : [];
    return assigned
      .map((h) => {
        const id = h?.id;
        const name = String(h?.name ?? '').trim();
        const last = String(h?.last_name ?? '').trim();
        const label = `${name} ${last}`.trim() || String(h?.name ?? h?.id ?? '').trim();
        return {id, label};
      })
      .filter((x) => x.id);
  }, [taskFull?.assigned_hacs]);

  const canRemove = React.useMemo(() => {
    if (!pmaId) return false;
    const creatorId = getCreatorId(taskFull);
    const ownerId = getOwnerId(taskFull);

    return [creatorId, ownerId]
      .filter(Boolean)
      .some((authorizedUserId) => String(pmaId) === String(authorizedUserId));
  }, [pmaId, taskFull]);

  const canManage = React.useMemo(() => {
    if (!pmaId) return false;
    return String(pmaId) === String(getOwnerId(taskFull) ?? '');
  }, [pmaId, taskFull]);

  const handleEditClick = (e) => {
    e?.stopPropagation?.();
    if (typeof onEdit === 'function') onEdit(taskFull);
    if (typeof onClose === 'function') onClose();
  };

  const handleRemoveClick = (e) => {
    e?.stopPropagation?.();
    if (typeof onRemove === 'function') onRemove(taskFull);
    if (typeof onClose === 'function') onClose();
  };

  const handleToggleCompleted = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (!token) return;
    const id = taskFull?.id ?? task?.id ?? null;
    if (!id) return;

    setIsCompleting(true);
    setOperationError('');
    if (typeof onCompletionStart === 'function') onCompletionStart(id);
    try {
      await apiClient.patch(
        `${process.env.REACT_APP_API_URL}/calendar/event/${id}`,
        {completed: true},
        {headers: {Authorization: `Bearer ${token}`}}
      );

      setTaskFull((prev) => ({...(prev || {}), completed: true}));

      if (typeof onCompletionSuccess === 'function') onCompletionSuccess(id);
      if (typeof onClose === 'function') onClose();
    } catch (err) {
      if (typeof onCompletionError === 'function') onCompletionError(id);
      setOperationError(t('calendar-operation-error'));
    } finally {
      setIsCompleting(false);
    }
  };

  const handleGoToHac = (hac) => {
    if (hac?.id) setSelectedMember(hac);
  };

  const handleMemberNavigation = React.useCallback(() => {
    saveCalendarReturnContext({
      sourcePath: window.location.pathname,
      task: taskFull || task,
      member: selectedMember,
    });
  }, [selectedMember, task, taskFull]);

  return (
    <Modal open={open} onClose={onClose} className="modal-task-view">
      <Box className="modal-task-view__dialog" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        {selectedMember ? (
          <CalendarMemberContext
            member={selectedMember}
            onBack={() => setSelectedMember(null)}
            onClose={onClose}
            onNavigateAway={handleMemberNavigation}
          />
        ) : (
          <ReminderDetailContent
            canManage={canManage}
            canRemove={canRemove}
            categoryId={taskFull?.category_id ?? taskFull?.category ?? 0}
            categoryLabel={categoryLabel}
            completedAtLabel={completedAtLabel}
            creatorLabel={creatorLabel}
            dateLine={dateLine}
            dateTimeLabel={dateTimeLabel}
            hacLabels={hacLabels}
            isCompleted={isCompleted}
            isCompleting={isCompleting}
            isSystemTask={isSystemCreator(taskFull)}
            onClose={onClose}
            onComplete={handleToggleCompleted}
            onEdit={handleEditClick}
            onMemberClick={handleGoToHac}
            onRemove={handleRemoveClick}
            ownerLabel={ownerLabel}
            task={taskFull}
            t={t}
          />
        )}

        <Snackbar open={Boolean(operationError)} autoHideDuration={4000} onClose={() => setOperationError('')}>
          <Alert severity="error" onClose={() => setOperationError('')} className="modal-task-view__error">
            {operationError}
          </Alert>
        </Snackbar>
      </Box>
    </Modal>
  );
}

export default ModalTaskView;
